import type { Membership } from '../../../lib/api';

/**
 * Per-tier Binary (Teslab variant) per-distribution income cap. Stored on each
 * tier as `config.distribution_caps.binary` — the Teslab variant clamps each
 * receiver's pairing income within a single distribution run to this amount and
 * flushes the excess. Blank / 0 = no cap. Lives under the Binary ComPlan editor
 * so all Binary config is in one place.
 */
export function BinaryDistributionCapPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  function getCap(cfg: Record<string, unknown>): string {
    const dc = cfg.distribution_caps as Record<string, unknown> | undefined;
    const v = dc?.binary;
    return typeof v === 'number' || typeof v === 'string' ? String(v) : '';
  }
  function setCap(id: string, raw: string) {
    const current = draft[id] ?? {};
    const dc = { ...((current.distribution_caps as Record<string, unknown> | undefined) ?? {}) };
    const n = Number(raw);
    if (raw.trim() === '' || Number.isNaN(n) || n <= 0) delete dc.binary;
    else dc.binary = n;
    const next = { ...current };
    if (Object.keys(dc).length === 0) delete next.distribution_caps;
    else next.distribution_caps = dc;
    onChange(id, next);
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground">
        Max pairing income a tier&apos;s slot can earn in a single Binary (Teslab) distribution run;
        the excess is flushed. Blank / 0 = no cap. Applies only to the Teslab variant.
      </p>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Tier</th>
              <th className="px-3 py-2 font-medium">Per-run income cap</th>
            </tr>
          </thead>
          <tbody>
            {memberships.map((m) => (
              <tr key={m.id} className="border-t">
                <td className="px-3 py-1.5 text-xs font-mono" title={m.name}>
                  {m.code}
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="number"
                    className="h-8 w-32 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                    value={getCap(draft[m.id] ?? {})}
                    disabled={readOnly}
                    step="any"
                    min={0}
                    placeholder="no cap"
                    onChange={(e) => setCap(m.id, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
